import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Clouds, Cloud } from "@react-three/drei";
import * as THREE from "three";
import { useStore, getEffectiveWeather } from "../store";

function Precip({ type }) {
  const ref = useRef();
  const { camera } = useThree();
  const isSnow = type === "snow";
  const count = useMemo(() => {
    const base = window.innerWidth < 820 ? 0.5 : 1;
    return Math.floor((isSnow ? 1400 : 2600) * base);
  }, [isSnow]);

  const { positions, speeds } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const speeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 260;
      positions[i * 3 + 1] = Math.random() * 240;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 260;
      speeds[i] = isSnow ? 6 + Math.random() * 6 : 90 + Math.random() * 60;
    }
    return { positions, speeds };
  }, [count, isSnow]);

  useFrame((state, delta) => {
    const g = ref.current;
    if (!g) return;
    const pos = g.geometry.attributes.position.array;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < count; i++) {
      pos[i * 3 + 1] -= speeds[i] * delta;
      if (isSnow) {
        pos[i * 3] += Math.sin(t + i) * delta * 3;
      }
      if (pos[i * 3 + 1] < 0) {
        pos[i * 3 + 1] = 240;
        pos[i * 3] = (Math.random() - 0.5) * 260;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 260;
      }
    }
    g.geometry.attributes.position.needsUpdate = true;
    // follow camera so field always surrounds the viewer
    g.position.set(camera.position.x, 0, camera.position.z);
  });

  return (
    <points ref={ref} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={isSnow ? 1.6 : 0.9}
        color={isSnow ? "#ffffff" : "#aebfca"}
        transparent
        opacity={isSnow ? 0.95 : 0.55}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

function DriftClouds() {
  // Static, rock-steady cloud layer. Skybox renders first (renderOrder 0); clouds render
  // after (renderOrder 1) with depthWrite disabled so they never z-fight the sky.
  const grp = useRef();
  useLayoutEffect(() => {
    grp.current?.traverse((o) => {
      if (o.material) {
        o.material.depthWrite = false;
        o.renderOrder = 1;
      }
    });
  });
  return (
    <group ref={grp} position={[0, 320, -140]} renderOrder={1}>
      <Clouds material={THREE.MeshBasicMaterial} limit={40} frustumCulled={false} renderOrder={1}>
        <Cloud seed={1} segments={26} bounds={[380, 40, 200]} volume={60} color="#ffffff" opacity={0.45} fade={120} speed={0} />
        <Cloud seed={7} position={[-460, 30, 160]} segments={20} bounds={[320, 34, 180]} volume={52} color="#f2f5f8" opacity={0.38} fade={120} speed={0} />
        <Cloud seed={12} position={[480, -20, -100]} segments={20} bounds={[340, 28, 180]} volume={48} color="#eef2f6" opacity={0.38} fade={120} speed={0} />
        <Cloud seed={21} position={[120, 10, 260]} segments={18} bounds={[300, 30, 160]} volume={44} color="#f6f9fb" opacity={0.34} fade={120} speed={0} />
      </Clouds>
    </group>
  );
}

export default function Weather() {
  const weather = useStore((s) => getEffectiveWeather(s));
  return (
    <group>
      {weather === "rain" && <Precip type="rain" />}
      {weather === "snow" && <Precip type="snow" />}
      {weather === "sunny" && <DriftClouds />}
    </group>
  );
}
